import { useState, useEffect, useCallback } from 'react';
import { Facility } from '../types/facility.types';
import { facilityApi } from '../api/facilityApi';

export const useFacilities = () => {
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFacilities = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await facilityApi.getAll();
      setFacilities(data);
    } catch (err) {
      console.error('Failed to fetch facilities:', err);
      setError('Failed to load facilities. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const createFacility = async (facilityData: Omit<Facility, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const newFacility = await facilityApi.create(facilityData);
      setFacilities(prev => [...prev, newFacility]);
      return { success: true };
    } catch (err) {
      console.error('Failed to create facility:', err);
      return { 
        success: false, 
        error: 'Failed to create facility. Please try again.' 
      };
    }
  };

  const updateFacility = async (id: string, updates: Partial<Facility>) => {
    try {
      const updatedFacility = await facilityApi.update(id, updates);
      setFacilities(prev => 
        prev.map(facility => 
          facility.id === id ? { ...facility, ...updatedFacility } : facility
        )
      );
      return { success: true };
    } catch (err) {
      console.error('Failed to update facility:', err);
      return { 
        success: false, 
        error: 'Failed to update facility. Please try again.' 
      };
    }
  };

  const deleteFacility = async (id: string) => {
    try {
      await facilityApi.delete(id);
      setFacilities(prev => prev.filter(facility => facility.id !== id));
      return { success: true };
    } catch (err) {
      console.error('Failed to delete facility:', err);
      return { 
        success: false, 
        error: 'Failed to delete facility. Please try again.' 
      };
    }
  };

  useEffect(() => {
    fetchFacilities();
  }, [fetchFacilities]);

  return {
    facilities,
    loading,
    error,
    createFacility,
    updateFacility,
    deleteFacility,
    refresh: fetchFacilities
  };
};
